import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, MessageCircle, FileDown, Check, X } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, Empty, Field, NativeSelect, PageHead, Pill } from "@/components/common";
import { FileUpload, Section, STATO_PERMESSO, downloadAuth } from "@/components/shared";

const EMPTY = { email: "", password: "", name: "", phone: "", mansione: "", active: true };

const DipDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}), password: "" }); }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    try {
      const payload = { ...f, password: f.password || null };
      f.id ? await api.put(`/admin/dipendenti/${f.id}`, payload) : await api.post("/admin/dipendenti", payload);
      toast.success("Dipendente salvato"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FAF8F5]" data-testid="dip-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl text-[#0D3B52]">{f.id ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nome e cognome *" className="col-span-2"><Input value={f.name} onChange={set("name")} className="bg-white" data-testid="dip-name" /></Field>
          <Field label="Email di accesso *"><Input value={f.email} onChange={set("email")} className="bg-white" data-testid="dip-email" /></Field>
          <Field label="Telefono (WhatsApp)"><Input value={f.phone} onChange={set("phone")} className="bg-white" data-testid="dip-phone" /></Field>
          <Field label="Mansione"><Input value={f.mansione} onChange={set("mansione")} className="bg-white" /></Field>
          <Field label={f.id ? "Nuova password" : "Password *"} hint="Min 8 caratteri"><Input value={f.password} onChange={set("password")} className="bg-white" data-testid="dip-password" /></Field>
        </div>
        <p className="text-xs text-[#5C6B73]">Il dipendente vede tutto tranne contabilità, fatture e impostazioni; può creare prenotazioni, clienti e DDT ma non modificare né eliminare.</p>
        <div className="flex justify-end"><Button onClick={save} data-testid="dip-save" className="rounded-full bg-[#0D3B52] hover:bg-[#082535] px-8">Salva</Button></div>
      </DialogContent>
    </Dialog>
  );
};

const Files = ({ dip }) => {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const load = useCallback(() => api.get(`/admin/dipendenti/${dip.id}/files`).then((r) => setFiles(r.data)), [dip.id]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-3 space-y-2" data-testid={`dip-files-${dip.id}`}>
      <div className="flex gap-2 items-center">
        <Input placeholder="Titolo (es. Busta paga Maggio 2026)" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white h-9 max-w-xs" data-testid={`dip-file-title-${dip.id}`} />
        <FileUpload url={`/admin/dipendenti/${dip.id}/files`} fields={{ title }} label="Carica busta paga / CUD" onDone={() => { setTitle(""); load(); }} testid={`dip-upload-${dip.id}`} />
      </div>
      {files.map((f) => (
        <div key={f.id} className="flex items-center justify-between text-sm border-t border-[#E2DDD5] pt-2">
          <span>{f.title} <span className="text-xs text-[#5C6B73]">· {fmtDate(f.created_at)}</span></span>
          <span className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => downloadAuth(`/staff/files/${f.id}/download`, f.original_filename)}><FileDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/admin/staff-files/${f.id}`); load(); }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </span>
        </div>
      ))}
    </div>
  );
};

const Permessi = () => {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/permessi").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (p, stato) => {
    const nota = stato === "rifiutato" ? window.prompt("Motivazione (facoltativa):") || "" : "";
    try {
      const { data } = await api.patch(`/admin/permessi/${p.id}`, { stato, nota });
      load();
      if (data.user_phone) window.open(data.whatsapp_url, "_blank");
      else toast.success("Aggiornato (il dipendente non ha un telefono per WhatsApp)");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Section title="Richieste ferie e permessi">
      {items.length === 0 ? <Empty>Nessuna richiesta.</Empty> : items.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-[#E2DDD5] py-3 text-sm" data-testid={`permesso-${p.id}`}>
          <div><p className="font-semibold text-[#0D3B52]">{p.user_name} · {p.tipo}</p><p className="text-[#5C6B73]">{fmtDate(p.dal)} → {fmtDate(p.al)} {p.motivo && `· ${p.motivo}`}</p></div>
          <div className="flex items-center gap-2">
            <Pill cls={STATO_PERMESSO[p.stato]}>{p.stato.replace("_", " ")}</Pill>
            {p.stato === "in_attesa" && <>
              <Button size="sm" onClick={() => decide(p, "approvato")} data-testid={`permesso-ok-${p.id}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4 mr-1" />OK</Button>
              <Button size="sm" variant="outline" onClick={() => decide(p, "rifiutato")} data-testid={`permesso-no-${p.id}`} className="rounded-full"><X className="h-4 w-4 mr-1" />No</Button>
            </>}
            {p.stato !== "in_attesa" && p.user_phone && <a href={p.whatsapp_url} target="_blank" rel="noreferrer" className="text-[#1DA851]"><MessageCircle className="h-4 w-4" /></a>}
          </div>
        </div>
      ))}
    </Section>
  );
};

export default function Dipendenti() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/dipendenti").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (user.role !== "admin") return <StaffArea />;
  const del = async (d) => { if (window.confirm(`Eliminare ${d.name}?`)) { await api.delete(`/admin/dipendenti/${d.id}`); load(); } };
  return (
    <div data-testid="admin-dipendenti">
      <PageHead eyebrow="Personale" title="Dipendenti" subtitle="Account con accesso limitato: vedono tutto tranne contabilità, creano ma non modificano. Qui gestisci ferie, permessi e buste paga.">
        <Button onClick={() => setEdit({})} data-testid="dip-new" className="rounded-full bg-[#0D3B52] hover:bg-[#082535]"><Plus className="h-4 w-4 mr-1" />Nuovo dipendente</Button>
      </PageHead>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {items.length === 0 ? <Empty>Nessun dipendente.</Empty> : items.map((d) => (
            <Card key={d.id} className="p-5" data-testid={`dip-row-${d.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-semibold text-[#0D3B52]">{d.name} {d.active === false && <Pill cls="bg-stone-100 text-stone-500 border-stone-200 ml-2">disattivato</Pill>}</p><p className="text-xs text-[#5C6B73]">{d.email} · {d.phone} · {d.mansione}</p></div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(d)} data-testid={`dip-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(d)} data-testid={`dip-delete-${d.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </div>
              <Files dip={d} />
            </Card>
          ))}
        </div>
        <Permessi />
      </div>
      <DipDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} initial={edit} onSaved={load} />
    </div>
  );
}

export const StaffArea = () => {
  const [perm, setPerm] = useState([]);
  const [files, setFiles] = useState([]);
  const [f, setF] = useState({ tipo: "ferie", dal: "", al: "", motivo: "" });
  const load = useCallback(() => { api.get("/staff/permessi").then((r) => setPerm(r.data)); api.get("/staff/files").then((r) => setFiles(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    if (!f.dal || !f.al) return toast.error("Inserisci le date");
    try { const { data } = await api.post("/staff/permessi", f); toast.success("Richiesta inviata"); window.open(data.whatsapp_url, "_blank"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="staff-area">
      <PageHead eyebrow="Area dipendente" title="Ferie, permessi e documenti" />
      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Richiedi ferie / permesso">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo"><NativeSelect value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} data-testid="perm-tipo"><option value="ferie">Ferie</option><option value="permesso">Permesso</option><option value="malattia">Malattia</option></NativeSelect></Field>
            <div />
            <Field label="Dal"><Input type="date" value={f.dal} onChange={(e) => setF({ ...f, dal: e.target.value })} className="bg-white" data-testid="perm-dal" /></Field>
            <Field label="Al"><Input type="date" value={f.al} onChange={(e) => setF({ ...f, al: e.target.value })} className="bg-white" data-testid="perm-al" /></Field>
            <Field label="Motivo" className="col-span-2"><Textarea rows={2} value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className="bg-white" /></Field>
          </div>
          <Button onClick={send} data-testid="perm-send" className="rounded-full bg-[#0D3B52] hover:bg-[#082535] mt-3">Invia richiesta</Button>
          <div className="mt-5 space-y-2">
            {perm.map((p) => <div key={p.id} className="flex justify-between text-sm border-t border-[#E2DDD5] pt-2"><span>{p.tipo} {fmtDate(p.dal)} → {fmtDate(p.al)}</span><Pill cls={STATO_PERMESSO[p.stato]}>{p.stato.replace("_", " ")}</Pill></div>)}
          </div>
        </Section>
        <Section title="Buste paga e documenti">
          {files.length === 0 ? <Empty>Nessun documento caricato.</Empty> : files.map((x) => (
            <div key={x.id} className="flex justify-between items-center text-sm border-b border-[#E2DDD5] py-2"><span>{x.title} <span className="text-xs text-[#5C6B73]">{fmtDate(x.created_at)}</span></span><Button size="icon" variant="ghost" onClick={() => downloadAuth(`/staff/files/${x.id}/download`, x.original_filename)}><FileDown className="h-4 w-4" /></Button></div>
          ))}
        </Section>
      </div>
    </div>
  );
};
